import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, IconButton } from "@mui/material";
import { Navigation } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperType } from "swiper";

import { Media } from "../../../../../types/types";
import { formatMediaUrl } from "../../../../../utils/formatters/formatMedia";

import { MediaSwipeRightIcon, MediaSwipeLeftIcon } from "../../../assets/icons/player-icons";

import { testimonialStyles } from "../styles";

const TestimonialCardSlider = ({ media }: { media: Media[] }) => {
  const [swiperInstance, setSwiperInstance] = useState<any>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <Box sx={testimonialStyles.card.slider.container}>
      <Swiper
        loop={false}
        modules={[Navigation]}
        onSwiper={(swiper: SwiperType) => {
          setSwiperInstance(swiper);
          swiper.on("slideChange", () => setActiveIndex(swiper.activeIndex));
        }}
        style={{ height: "100%" }}
      >
        {media.map((item) => (
          <SwiperSlide key={item.id}>
            <MediaSlide media={item} />
          </SwiperSlide>
        ))}
      </Swiper>

      {media.length > 1 && (
        <>
          <SliderNavigation swiper={swiperInstance} />
          <SliderPaginationBullets swiper={swiperInstance} count={media.length} activeIndex={activeIndex} />
        </>
      )}
    </Box>
  );
};

export default TestimonialCardSlider;

const MediaSlide = ({ media }: { media: Media }) => {
  return (
    <Box sx={testimonialStyles.card.slider.mediaContainer}>
      {media.type === "VIDEO" ? (
        <video
          src={formatMediaUrl(media.url)}
          controls
          style={{ ...testimonialStyles.card.slider.media, objectFit: "contain" }}
        />
      ) : (
        <img
          src={formatMediaUrl(media.url)}
          alt={`media-${media.id}`}
          style={{ ...testimonialStyles.card.slider.media, objectFit: "contain" }}
          loading="lazy"
        />
      )}
    </Box>
  );
};

const SliderNavigation = ({ swiper }: { swiper: any }) => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language;

  const handlePrev = () => {
    if (swiper) swiper.slidePrev();
  };

  const handleNext = () => {
    if (swiper) swiper.slideNext();
  };

  return (
    <>
      <IconButton
        onClick={handlePrev}
        sx={{
          ...testimonialStyles.card.slider.navigationButton,
          left: 16,
        }}
      >
        {currentLang === "en" ? <MediaSwipeLeftIcon /> : <MediaSwipeRightIcon />}
      </IconButton>

      <IconButton
        onClick={handleNext}
        sx={{
          ...testimonialStyles.card.slider.navigationButton,
          right: 16,
        }}
      >
        {currentLang === "en" ? <MediaSwipeRightIcon /> : <MediaSwipeLeftIcon />}
      </IconButton>
    </>
  );
};

const SliderPaginationBullets = ({
  swiper,
  count,
  activeIndex,
}: {
  swiper: any;
  count: number;
  activeIndex: number;
}) => {
  if (!swiper) return null;

  return (
    <Box sx={testimonialStyles.card.slider.paginationContainer}>
      {Array.from({ length: count }).map((_, index) => (
        <Box
          key={index}
          onClick={() => swiper.slideTo(index)}
          sx={testimonialStyles.card.slider.paginationDot(index === activeIndex)}
        />
      ))}
    </Box>
  );
};
